import React, { useState, useEffect, useCallback, useRef } from 'react';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import TableSettings from '../../shared/components/TableSettings';
import DateRangePicker from '../../ReportingDashboard/components/DateRangePicker';
import client from '../../../shared/api/client';
import { toastError } from '../../../shared/toastHelper';

const qs = require('qs');

const TYPE_OPTIONS = [
  { key: 'order', label: 'Orders' },
  { key: 'add_on', label: 'Add-ons' },
  { key: 'membership', label: 'Memberships' },
  { key: 'donation', label: 'Donations' },
  { key: 'pass', label: 'Passes' },
  { key: 'pack', label: 'Packs' },
  { key: 'class', label: 'Classes' },
  { key: 'gift_card', label: 'Gift cards' },
];

const STATUS_LABELS = {
  paid: 'Paid',
  refunded: 'Refunded',
  partial_refund: 'Partial refund',
};

const STATUS_STYLES = {
  paid: { dot: 'var(--sl-fg-success)', color: 'var(--sl-text-success)' },
  refunded: { dot: 'var(--sl-fg-error)', color: 'var(--sl-text-error)' },
  partial_refund: { dot: 'var(--sl-fg-warning)', color: 'var(--sl-text-warning)' },
};

const TYPE_BADGE = {
  order: { label: 'Order', bg: 'var(--sl-bg-brand-primary)', color: 'var(--sl-text-brand)' },
  add_on: { label: 'Add-on', bg: 'var(--sl-bg-tertiary)', color: 'var(--sl-text-secondary)' },
  membership: { label: 'Membership', bg: 'var(--sl-bg-warning-primary)', color: 'var(--sl-text-warning)' },
  donation: { label: 'Donation', bg: 'var(--sl-bg-success-primary)', color: 'var(--sl-text-success)' },
  pass: { label: 'Pass', bg: 'var(--sl-bg-brand-secondary)', color: 'var(--sl-text-brand)' },
  pack: { label: 'Pack', bg: 'var(--sl-bg-error-primary)', color: 'var(--sl-text-error)' },
  class: { label: 'Class', bg: 'var(--sl-bg-secondary)', color: 'var(--sl-text-brand)' },
  gift_card: { label: 'Gift card', bg: 'var(--sl-bg-brand-solid)', color: 'var(--sl-text-white)' },
};

const AVATAR_COLORS = [
  ['var(--sl-bg-brand-primary)', 'var(--sl-text-brand)'],
  ['var(--sl-bg-success-primary)', 'var(--sl-text-success)'],
  ['var(--sl-bg-warning-primary)', 'var(--sl-text-warning)'],
  ['var(--sl-bg-error-primary)', 'var(--sl-text-error)'],
];

const DEFAULT_VISIBLE_COLUMNS = [
  { id: 'buyer', label: 'Buyer', alwaysVisible: true },
  { id: 'order_number', label: 'Order' },
  { id: 'types', label: 'Type' },
  { id: 'event', label: 'Event' },
  { id: 'amount', label: 'Amount' },
  { id: 'status', label: 'Status' },
];

const DEFAULT_HIDDEN_COLUMNS = [
  { id: 'fan_phone', label: 'Phone number' },
  { id: 'zip', label: 'Zip code' },
  { id: 'source', label: 'Source' },
  { id: 'card_brand', label: 'Card brand' },
  { id: 'card_last4', label: 'Card last 4' },
];

const DEFAULT_STATE = {
  search: '',
  types: TYPE_OPTIONS.map((t) => t.key),
  refundsOnly: false,
  venueOwnershipId: [],
  calendarEventId: [],
  orderStart: '',
  orderEnd: '',
  eventStart: '',
  eventEnd: '',
  zip: '',
  cardLast4: '',
  page: 1,
  perPage: 20,
};

export function buildTransactionsParams(s) {
  const params = { page: s.page, per_page: s.perPage };
  if (s.search) params.search = s.search;
  if (s.types && s.types.length && s.types.length < TYPE_OPTIONS.length) params.type = s.types;
  if (s.refundsOnly) params.refunds_only = true;
  if (s.venueOwnershipId && s.venueOwnershipId.length) params.venue_ownership_id = s.venueOwnershipId;
  if (s.calendarEventId && s.calendarEventId.length) params.calendar_event_id = s.calendarEventId;
  if (s.orderStart && s.orderEnd) { params.start_date = s.orderStart; params.end_date = s.orderEnd; }
  if (s.eventStart && s.eventEnd) { params.event_start_date = s.eventStart; params.event_end_date = s.eventEnd; }
  if (s.zip) params.zip = s.zip;
  if (s.cardLast4) params.card_last4 = s.cardLast4;
  return params;
}

export function renderStatusLabel(status) {
  return STATUS_LABELS[status] || status;
}

const currency = (n) => `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STAT_LABEL = { fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--sl-text-tertiary)' };

export function ResultsSummary({ count, totalAmount }) {
  return (
    <div className="d-flex align-items-center" style={{ gap: 20 }}>
      <div className="d-flex flex-column" style={{ gap: 2 }}>
        <span style={STAT_LABEL}>Transactions</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--sl-fg-brand)' }}>{count.toLocaleString()}</span>
      </div>
      <span aria-hidden="true" style={{ width: 1, height: 32, background: 'var(--sl-bg-tertiary)' }} />
      <div className="d-flex flex-column" style={{ gap: 2 }}>
        <span style={STAT_LABEL}>Total amount</span>
        {typeof totalAmount === 'number' ? (
          <span style={{ fontSize: 16, fontWeight: 700, color: totalAmount < 0 ? 'var(--sl-fg-error)' : 'var(--sl-text-primary)' }}>{currency(totalAmount)}</span>
        ) : (
          <span
            title={totalAmount === null ? 'Too many transactions to total. Narrow the filters to see it.' : undefined}
            style={{ fontSize: 16, fontWeight: 700, color: 'var(--sl-text-tertiary)' }}
          >
            —
          </span>
        )}
      </div>
    </div>
  );
}

function initials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

function avatarPalette(name) {
  const sum = (name || '?').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

function last30() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { start: fmt(start), end: fmt(end) };
}

const FILTER_LABEL = { fontSize: 13, fontWeight: 500, color: 'var(--sl-text-secondary)', marginBottom: 4 };
const CARD_STYLE = { background: 'var(--sl-bg-primary)', border: '1px solid var(--sl-border-secondary)', borderRadius: 12 };
const TH_STYLE = { fontWeight: 500, fontSize: 12, color: 'var(--sl-text-tertiary)', textTransform: 'uppercase' };
const SUB_STYLE = { fontSize: 12, color: 'var(--sl-text-tertiary)' };

function useIsMobile(maxWidth = 767) {
  const query = `(max-width: ${maxWidth}px)`;
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    if (mql.addEventListener) { mql.addEventListener('change', handler); } else { mql.addListener(handler); }
    return () => {
      if (mql.removeEventListener) { mql.removeEventListener('change', handler); } else { mql.removeListener(handler); }
    };
  }, [query]);
  return isMobile;
}

function TransactionsPage({ team, venueOwnerships = [], crmEnabled = false }) {
  const isMobile = useIsMobile();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const initial = last30();
  const [filters, setFilters] = useState({ ...DEFAULT_STATE, orderStart: initial.start, orderEnd: initial.end });
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_entries: 0, per_page: 20 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [eventOptions, setEventOptions] = useState([]);
  const [venueOptions] = useState(
    venueOwnerships.map((v) => ({ value: String(v.id), label: v.name }))
  );
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [hiddenColumns, setHiddenColumns] = useState(DEFAULT_HIDDEN_COLUMNS);
  const searchDebounce = useRef(null);
  const requestId = useRef(0);
  const totalRequestId = useRef(0);
  const [totalAmount, setTotalAmount] = useState(undefined);
  const [searchDraft, setSearchDraft] = useState(filters.search);

  const update = (patch) => setFilters((prev) => ({ ...prev, ...patch, page: patch.page || 1 }));

  useEffect(() => {
    const dateParams = {};
    if (filters.orderStart && filters.orderEnd) { dateParams.start_date = filters.orderStart; dateParams.end_date = filters.orderEnd; }
    if (filters.eventStart && filters.eventEnd) { dateParams.event_start_date = filters.eventStart; dateParams.event_end_date = filters.eventEnd; }
    client.get(`/teams/${team.id}/transactions/filter_options.json`, {
      params: dateParams,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets' }),
    }).then((res) => {
      if (res.data.event_options) setEventOptions(res.data.event_options);
    }).catch(() => toastError('Could not load filter options.'));
  }, [team.id, filters.orderStart, filters.orderEnd, filters.eventStart, filters.eventEnd]);

  const fetchData = useCallback(() => {
    const myId = requestId.current + 1;
    requestId.current = myId;
    setLoading(true);
    setLoadError(false);
    client.get(`/teams/${team.id}/transactions/data.json`, {
      params: buildTransactionsParams(filters),
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets' }),
    }).then((res) => {
      if (myId !== requestId.current) return;
      setRows(res.data.transactions || []);
      setPagination(res.data.pagination || { current_page: 1, total_pages: 1, total_entries: 0, per_page: 20 });
    }).catch(() => {
      if (myId !== requestId.current) return;
      setLoadError(true);
      toastError('Could not load transactions. Please try again.');
    }).finally(() => {
      if (myId === requestId.current) setLoading(false);
    });
  }, [team.id, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { page: _page, per_page: _perPage, ...totalParams } = buildTransactionsParams(filters);
  const totalQuery = qs.stringify(totalParams, { arrayFormat: 'brackets' });

  useEffect(() => {
    const myId = totalRequestId.current + 1;
    totalRequestId.current = myId;
    setTotalAmount(undefined);
    client.get(`/teams/${team.id}/transactions/total.json?${totalQuery}`)
      .then((res) => { if (myId === totalRequestId.current) setTotalAmount(res.data.total_amount); })
      .catch(() => { if (myId === totalRequestId.current) setTotalAmount('error'); });
  }, [team.id, totalQuery]);

  const onSearchChange = (value) => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => update({ search: value }), 300);
  };

  const toggleType = (key) => {
    setFilters((prev) => {
      const has = prev.types.includes(key);
      const types = has ? prev.types.filter((t) => t !== key) : [...prev.types, key];
      if (types.length === 0) return prev;
      return { ...prev, types, page: 1 };
    });
  };

  const reset = () => {
    setFilters({ ...DEFAULT_STATE, orderStart: initial.start, orderEnd: initial.end });
    setSearchDraft('');
  };

  useEffect(() => () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); }, []);

  const exportUrl = `/teams/${team.id}/transactions/export.csv?${qs.stringify(
    buildTransactionsParams(filters), { arrayFormat: 'brackets' }
  )}`;

  const isVisible = (id) => visibleColumns.some((c) => c.id === id);
  const fanHref = (r) => {
    if (!r.fan_id) return null;
    return crmEnabled
      ? `/teams/${team.id}/crm/fans/${r.fan_id}`
      : `/teams/${team.id}/fans/${r.fan_id}`;
  };
  const visibleCount = visibleColumns.length;

  const renderTypeBadges = (types) => (types || []).map((t) => {
    const cfg = TYPE_BADGE[t] || { label: t, bg: 'var(--sl-bg-tertiary)', color: 'var(--sl-text-secondary)' };
    return (
      <span key={t} style={{
        display: 'inline-block', background: cfg.bg, color: cfg.color,
        fontSize: 12, fontWeight: 500, borderRadius: 16, padding: '2px 10px', marginRight: 6,
      }}>
        {cfg.label}
      </span>
    );
  });

  const renderStatus = (status) => {
    const cfg = STATUS_STYLES[status] || { dot: 'var(--sl-text-tertiary)', color: 'var(--sl-text-secondary)' };
    return (
      <span className="d-flex align-items-center" style={{ gap: 6, color: cfg.color, fontWeight: 500, fontSize: 13 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
        {renderStatusLabel(status)}
      </span>
    );
  };

  const columnDefs = {
    buyer: {
      header: 'Buyer',
      headerStyle: { ...TH_STYLE, paddingLeft: 24 },
      cell: (r) => {
        const [bg, fg] = avatarPalette(r.buyer_name);
        return (
          <td style={{ paddingLeft: 24 }}>
            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              <span style={{ width: 32, height: 32, borderRadius: '50%', background: bg, color: fg, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {initials(r.buyer_name)}
              </span>
              <div style={{ minWidth: 0 }}>
                {fanHref(r) ? (
                  <a href={fanHref(r)} style={{ fontWeight: 600, color: 'var(--sl-text-primary)', textDecoration: 'none' }}>{r.buyer_name}</a>
                ) : (
                  <div style={{ fontWeight: 600, color: 'var(--sl-text-primary)' }}>{r.buyer_name}</div>
                )}
                <div style={SUB_STYLE}>{r.buyer_email}</div>
              </div>
            </div>
          </td>
        );
      },
    },
    order_number: {
      header: 'Order',
      headerStyle: TH_STYLE,
      cell: (r) => (
        <td>
          {r.order_number ? (
            <>
              <a href={`/teams/${team.id}/orders/${r.id}`} style={{ color: 'var(--sl-fg-brand)', fontWeight: 500, textDecoration: 'none' }}>#{r.order_number}</a>
              <div style={SUB_STYLE}>{r.order_date ? new Date(r.order_date).toLocaleDateString() : ''}</div>
            </>
          ) : r.membership_token ? (
            <a href={`/teams/${team.id}/memberships/${r.membership_token}`} style={{ color: 'var(--sl-fg-brand)', fontWeight: 500, textDecoration: 'none' }}>{r.order_date ? new Date(r.order_date).toLocaleDateString() : ''}</a>
          ) : (
            <div style={SUB_STYLE}>{r.order_date ? new Date(r.order_date).toLocaleDateString() : ''}</div>
          )}
        </td>
      ),
    },
    types: {
      header: 'Type',
      headerStyle: TH_STYLE,
      cell: (r) => <td>{renderTypeBadges(r.types)}</td>,
    },
    event: {
      header: 'Event',
      headerStyle: TH_STYLE,
      cell: (r) => (
        <td>
          <div style={{ color: 'var(--sl-text-primary)' }}>{r.event_name}</div>
          <div style={SUB_STYLE}>{r.venue_name}</div>
        </td>
      ),
    },
    amount: {
      header: 'Amount',
      headerStyle: { ...TH_STYLE, textAlign: 'right' },
      cell: (r) => (
        <td style={{ textAlign: 'right', fontWeight: 500, color: r.amount < 0 ? 'var(--sl-fg-error)' : 'var(--sl-text-primary)' }}>
          {currency(r.amount)}
        </td>
      ),
    },
    status: {
      header: 'Status',
      headerStyle: TH_STYLE,
      cell: (r) => <td>{renderStatus(r.status)}</td>,
    },
    fan_phone: {
      header: 'Phone number',
      headerStyle: TH_STYLE,
      cell: (r) => <td style={{ color: 'var(--sl-text-secondary)' }}>{r.fan_phone || '—'}</td>,
    },
    zip: {
      header: 'Zip code',
      headerStyle: TH_STYLE,
      cell: (r) => <td style={{ color: 'var(--sl-text-secondary)' }}>{r.zip || '—'}</td>,
    },
    source: {
      header: 'Source',
      headerStyle: TH_STYLE,
      cell: (r) => <td style={{ color: 'var(--sl-text-secondary)' }}>{r.source || '—'}</td>,
    },
    card_brand: {
      header: 'Card brand',
      headerStyle: TH_STYLE,
      cell: (r) => <td style={{ color: 'var(--sl-text-secondary)' }}>{r.card_brand || '—'}</td>,
    },
    card_last4: {
      header: 'Card last 4',
      headerStyle: TH_STYLE,
      cell: (r) => <td style={{ color: 'var(--sl-text-secondary)' }}>{r.card_last4 || '—'}</td>,
    },
  };

  const orderedColumns = visibleColumns.filter((c) => columnDefs[c.id]);

  return (
    <div style={{ padding: '8px 0 24px' }}>
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ marginBottom: 24, flexWrap: 'wrap', gap: 12 }}
      >
        <h1 style={{ fontWeight: 700, fontSize: 28, margin: 0, color: 'var(--sl-text-primary)' }}>Transactions</h1>
        <a href={exportUrl} className="btn-setlist-secondary d-flex align-items-center" style={{ gap: 8 }}>
          <i className="fas fa-download" style={{ fontSize: 13 }} /> Export
        </a>
      </div>

      <div style={{ display: 'flex', gap: 15, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ ...CARD_STYLE, flex: '1 1 240px', padding: 20 }}>
          <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: (!isMobile || filtersOpen) ? 16 : 0 }}>
            {isMobile ? (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 d-flex align-items-center"
                style={{ fontWeight: 700, gap: 8, color: 'var(--sl-text-primary)', textDecoration: 'none' }}
                onClick={() => setFiltersOpen((o) => !o)}
              >
                Filters <i className={`fas fa-chevron-${filtersOpen ? 'up' : 'down'}`} style={{ fontSize: 12 }} />
              </button>
            ) : (
              <h6 style={{ fontWeight: 700, margin: 0, color: 'var(--sl-text-primary)' }}>Filters</h6>
            )}
            {(!isMobile || filtersOpen) && (
              <button type="button" className="btn btn-link btn-sm p-0" style={{ fontWeight: 500 }} onClick={reset}>Reset</button>
            )}
          </div>

          {(!isMobile || filtersOpen) && (
            <>
          <div style={FILTER_LABEL}><label htmlFor="txn-search" style={{ margin: 0 }}>Search</label></div>
          <input
            type="text"
            id="txn-search"
            className="form-control form-control-sm mb-3"
            placeholder="Name, email, or order #"
            value={searchDraft}
            onChange={(e) => { setSearchDraft(e.target.value); onSearchChange(e.target.value); }}
          />

          <div className="mb-3">
            <div style={FILTER_LABEL}>Type</div>
            {TYPE_OPTIONS.map((t) => (
              <div className="form-check" key={t.key}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`type-${t.key}`}
                  checked={filters.types.includes(t.key)}
                  onChange={() => toggleType(t.key)}
                />
                <label className="form-check-label" htmlFor={`type-${t.key}`}>{t.label}</label>
              </div>
            ))}
            <div className="form-check mt-1">
              <input
                type="checkbox"
                className="form-check-input"
                id="refunds-only"
                checked={filters.refundsOnly}
                onChange={(e) => update({ refundsOnly: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="refunds-only">Refunds only</label>
            </div>
          </div>

          <div style={FILTER_LABEL}><label htmlFor="txn-venue" style={{ margin: 0 }}>Venue</label></div>
          <select
            id="txn-venue"
            className="form-control form-control-sm mb-3"
            value={filters.venueOwnershipId[0] || ''}
            onChange={(e) => update({ venueOwnershipId: e.target.value ? [e.target.value] : [] })}
          >
            <option value="">All venues</option>
            {venueOptions.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
          </select>

          <div style={FILTER_LABEL}><label htmlFor="txn-event" style={{ margin: 0 }}>Event</label></div>
          <select
            id="txn-event"
            className="form-control form-control-sm mb-3"
            value={filters.calendarEventId[0] || ''}
            onChange={(e) => update({ calendarEventId: e.target.value ? [e.target.value] : [] })}
          >
            <option value="">Any event</option>
            {eventOptions.map((ev) => <option key={ev.value} value={ev.value}>{ev.label}</option>)}
          </select>

          <div style={FILTER_LABEL}>Order date</div>
          <div className="mb-3">
            <DateRangePicker
              dateRange={{ start: filters.orderStart, end: filters.orderEnd }}
              onChange={(r) => update({ orderStart: r.start || '', orderEnd: r.end || '' })}
              emptyLabel="All dates"
              buttonStyle={{ width: '100%' }}
            />
          </div>

          <div style={FILTER_LABEL}>Event date</div>
          <div className="mb-3">
            <DateRangePicker
              dateRange={{ start: filters.eventStart, end: filters.eventEnd }}
              onChange={(r) => update({ eventStart: r.start || '', eventEnd: r.end || '' })}
              emptyLabel="All dates"
              buttonStyle={{ width: '100%' }}
            />
          </div>

          <div style={FILTER_LABEL}><label htmlFor="txn-zip" style={{ margin: 0 }}>Zip code</label></div>
          <input type="text" id="txn-zip" className="form-control form-control-sm mb-3" placeholder="Any"
            value={filters.zip} onChange={(e) => update({ zip: e.target.value })} />

          <div style={FILTER_LABEL}><label htmlFor="txn-card-last4" style={{ margin: 0 }}>Card last 4</label></div>
          <input type="text" id="txn-card-last4" className="form-control form-control-sm mb-3" placeholder="Any"
            inputMode="numeric" maxLength={4}
            value={filters.cardLast4}
            onChange={(e) => update({ cardLast4: e.target.value.replace(/\D/g, '').slice(0, 4) })} />

          <div className="d-flex align-items-center" style={{ gap: 6, ...SUB_STYLE, color: 'var(--sl-fg-brand)', paddingTop: 8, borderTop: '1px solid var(--sl-bg-tertiary)' }}>
            <i className="fas fa-sync" style={{ fontSize: 11 }} /> Results update as you select
          </div>
            </>
          )}
        </div>

        <div style={{ ...CARD_STYLE, flex: '999 1 320px', minWidth: 0 }}>
          <div className="d-flex align-items-center justify-content-between" style={{ padding: '16px 24px', borderBottom: '1px solid var(--sl-bg-tertiary)', flexWrap: 'wrap', gap: 12 }}>
            <div className="d-flex align-items-center" style={{ gap: 24, flexWrap: 'wrap' }}>
              <h5 style={{ fontWeight: 700, fontSize: 18, margin: 0, color: 'var(--sl-text-primary)' }}>Results</h5>
              {/* WHY: a table that loaded empty sums to $0 even when the total query timed out on a large team. */}
              <ResultsSummary count={pagination.total_entries} totalAmount={!loading && !loadError && pagination.total_entries === 0 ? 0 : totalAmount} />
            </div>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="btn btn-setlist-secondary"
                onClick={() => setTableSettingsOpen((o) => !o)}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 7.5V2.5M5 17.5V12.5M5 12.5C5.46499 12.5 5.69748 12.5 5.88823 12.4239C6.1305 12.3224 6.32239 12.1305 6.42388 11.8882C6.5 11.6975 6.5 11.465 6.5 11C6.5 10.535 6.5 10.3025 6.42388 10.1118C6.32239 9.8695 6.1305 9.67761 5.88823 9.57612C5.69748 9.5 5.46499 9.5 5 9.5C4.53501 9.5 4.30252 9.5 4.11177 9.57612C3.8695 9.67761 3.67761 9.8695 3.57612 10.1118C3.5 10.3025 3.5 10.535 3.5 11C3.5 11.465 3.5 11.6975 3.57612 11.8882C3.67761 12.1305 3.8695 12.3224 4.11177 12.4239C4.30252 12.5 4.53501 12.5 5 12.5ZM10 17.5V10M10 10C10.465 10 10.6975 10 10.8882 9.92388C11.1305 9.82239 11.3224 9.6305 11.4239 9.38823C11.5 9.19748 11.5 8.96499 11.5 8.5C11.5 8.03501 11.5 7.80252 11.4239 7.61177C11.3224 7.3695 11.1305 7.17761 10.8882 7.07612C10.6975 7 10.465 7 10 7C9.53501 7 9.30252 7 9.11177 7.07612C8.8695 7.17761 8.67761 7.3695 8.57612 7.61177C8.5 7.80252 8.5 8.03501 8.5 8.5C8.5 8.96499 8.5 9.19748 8.57612 9.38823C8.67761 9.6305 8.8695 9.82239 9.11177 9.92388C9.30252 10 9.53501 10 10 10ZM10 7V2.5M15 17.5V15M15 15C15.465 15 15.6975 15 15.8882 14.9239C16.1305 14.8224 16.3224 14.6305 16.4239 14.3882C16.5 14.1975 16.5 13.965 16.5 13.5C16.5 13.035 16.5 12.8025 16.4239 12.6118C16.3224 12.3695 16.1305 12.1776 15.8882 12.0761C15.6975 12 15.465 12 15 12C14.535 12 14.3025 12 14.1118 12.0761C13.8695 12.1776 13.6776 12.3695 13.5761 12.6118C13.5 12.8025 13.5 13.035 13.5 13.5C13.5 13.965 13.5 14.1975 13.5761 14.3882C13.6776 14.6305 13.8695 14.8224 14.1118 14.9239C14.3025 15 14.535 15 15 15ZM15 12V2.5"
                    stroke="currentColor"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Table Settings</span>
              </button>
              <TableSettings
                isOpen={tableSettingsOpen}
                onClose={() => setTableSettingsOpen(false)}
                visibleFields={visibleColumns}
                hiddenFields={hiddenColumns}
                onFieldsChange={(v, h) => { setVisibleColumns(v); setHiddenColumns(h); }}
              />
            </div>
          </div>

          {isMobile ? (
            <div>
              {loading ? (
                <div className="text-center py-4 text-muted">Loading…</div>
              ) : loadError ? (
                <div className="text-center py-4 text-muted">Could not load transactions. Please try again.</div>
              ) : rows.length === 0 ? (
                <div className="text-center py-4 text-muted">No transactions found.</div>
              ) : rows.map((r) => {
                const [bg, fg] = avatarPalette(r.buyer_name);
                return (
                  <div key={r.id} style={{ padding: '14px 16px', borderTop: '1px solid var(--sl-bg-tertiary)' }}>
                    <div className="d-flex justify-content-between align-items-start" style={{ gap: 12 }}>
                      <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 0 }}>
                        <span style={{ width: 36, height: 36, borderRadius: '50%', background: bg, color: fg, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {initials(r.buyer_name)}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          {fanHref(r) ? (
                            <a href={fanHref(r)} style={{ fontWeight: 600, color: 'var(--sl-text-primary)', textDecoration: 'none' }}>{r.buyer_name}</a>
                          ) : (
                            <div style={{ fontWeight: 600, color: 'var(--sl-text-primary)' }}>{r.buyer_name}</div>
                          )}
                          <div style={{ ...SUB_STYLE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.buyer_email}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                        <span style={{ fontWeight: 600, color: r.amount < 0 ? 'var(--sl-fg-error)' : 'var(--sl-text-primary)' }}>{currency(r.amount)}</span>
                        <span style={{ marginTop: 4 }}>{renderStatus(r.status)}</span>
                      </div>
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <div style={{ color: 'var(--sl-text-primary)', fontSize: 14 }}>{r.event_name}</div>
                      <div style={SUB_STYLE}>{r.venue_name}</div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
                      {r.order_number ? (
                        <a href={`/teams/${team.id}/orders/${r.id}`} style={{ color: 'var(--sl-fg-brand)', fontWeight: 500, textDecoration: 'none', fontSize: 13 }}>
                          #{r.order_number} <span style={SUB_STYLE}>· {r.order_date ? new Date(r.order_date).toLocaleDateString() : ''}</span>
                        </a>
                      ) : r.membership_token ? (
                        <a href={`/teams/${team.id}/memberships/${r.membership_token}`} style={{ color: 'var(--sl-fg-brand)', fontWeight: 500, textDecoration: 'none', fontSize: 13 }}>
                          {r.order_date ? new Date(r.order_date).toLocaleDateString() : ''}
                        </a>
                      ) : (
                        <span style={{ ...SUB_STYLE, fontSize: 13 }}>{r.order_date ? new Date(r.order_date).toLocaleDateString() : ''}</span>
                      )}
                      <div>{renderTypeBadges(r.types)}</div>
                    </div>
                    {((isVisible('fan_phone') && r.fan_phone) || (isVisible('zip') && r.zip)) && (
                      <div className="d-flex" style={{ gap: 16, marginTop: 8, ...SUB_STYLE }}>
                        {isVisible('fan_phone') && r.fan_phone && <span>Phone: {r.fan_phone}</span>}
                        {isVisible('zip') && r.zip && <span>Zip: {r.zip}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  {orderedColumns.map((c) => (
                    <th key={c.id} style={columnDefs[c.id].headerStyle}>{columnDefs[c.id].header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={visibleCount} className="text-center py-4 text-muted">Loading…</td></tr>
                ) : loadError ? (
                  <tr><td colSpan={visibleCount} className="text-center py-4 text-muted">Could not load transactions. Please try again.</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={visibleCount} className="text-center py-4 text-muted">No transactions found.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id}>
                    {orderedColumns.map((c) => React.cloneElement(columnDefs[c.id].cell(r), { key: c.id }))}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}

          {pagination.total_pages > 1 && (
            <div style={{ padding: '12px 24px', borderTop: '1px solid var(--sl-bg-tertiary)' }}>
              <TeamPagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                totalItems={pagination.total_entries}
                pageSize={pagination.per_page}
                itemLabel="transactions"
                onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TransactionsPage;
